import { useState } from "react";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const VISA_TYPES = [
  "Visitor Visa (Subclass 600)",
  "Student Visa (Subclass 500)",
  "Temporary Graduate (Subclass 485)",
  "Partner Visa (820/801 · 309/100)",
  "Parent Visa (143 · 103 · 804)",
  "Skilled Independent (Subclass 189)",
  "Skilled Nominated (Subclass 190)",
  "Skilled Regional (Subclass 491)",
  "Employer Sponsored (482 · 186 · 494)",
  "Citizenship by Conferral / Descent",
  "Visa Refusal / ART Review",
  "Other / Not sure",
];

export default function PricingInquiryForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", visa_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.visa_type) {
      toast.error("Please provide your email and select a visa type.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent statement to continue.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/contact", {
        name: form.name || "Pricing Inquiry",
        email: form.email,
        phone: form.phone,
        subject: `Pricing Inquiry — ${form.visa_type}`,
        message: form.message || `Pricing inquiry for ${form.visa_type}.`,
        consent: true,
        consent_timestamp: new Date().toISOString(),
      });
      toast.success("Thanks. We'll contact you within 24 hours with a personalised quote.");
      setForm({ name: "", email: "", phone: "", visa_type: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="pricing-inquiry-form"
      onSubmit={onSubmit}
      className={`bg-white border-2 border-[#C43A89] ${compact ? "p-6 md:p-7" : "p-7 md:p-10"} relative shadow-[0_20px_60px_-15px_rgba(196,58,137,0.35)] ring-1 ring-[#C43A89]/10`}
    >
      <span aria-hidden className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
      <span aria-hidden className="absolute -top-3 right-6 bg-[#C43A89] text-white text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 shadow-md">
        Free Quote
      </span>

      <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">
        Ask About Pricing
      </div>
      <h3 className="font-display text-2xl md:text-3xl text-[#29324A] leading-tight">
        Get a personalised quote for your visa application
      </h3>
      <p className="mt-3 text-sm text-slate-500">
        Tell us a little about your situation. We'll respond within 24 business hours.
      </p>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          data-testid="pricing-name"
          type="text"
          placeholder="Your name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          className="w-full border border-slate-200 px-4 py-3 text-sm focus:border-[#29324A] focus:outline-none"
        />
        <input
          data-testid="pricing-email"
          required
          type="email"
          placeholder="Email address *"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className="w-full border border-slate-200 px-4 py-3 text-sm focus:border-[#29324A] focus:outline-none"
        />
        <input
          data-testid="pricing-phone"
          type="tel"
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          className="w-full border border-slate-200 px-4 py-3 text-sm focus:border-[#29324A] focus:outline-none"
        />
        <select
          data-testid="pricing-visa-type"
          required
          value={form.visa_type}
          onChange={(e) => update("visa_type", e.target.value)}
          className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:border-[#29324A] focus:outline-none"
        >
          <option value="">Select visa type *</option>
          {VISA_TYPES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>

      <textarea
        data-testid="pricing-message"
        rows={4}
        placeholder="Your question or message"
        value={form.message}
        onChange={(e) => update("message", e.target.value)}
        className="mt-4 w-full border border-slate-200 px-4 py-3 text-sm focus:border-[#29324A] focus:outline-none"
      />

      <ConsentCheckbox value={consent} onChange={setConsent} testid="pricing-consent" />

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-[11px] text-slate-400 tracking-wide">
          We'll contact you within 24 hours.
        </p>
        <button
          data-testid="pricing-submit"
          disabled={loading || !consent}
          className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] disabled:opacity-60 text-white text-xs font-bold tracking-wider uppercase px-7 py-3.5 transition-colors btn-shimmer"
        >
          {loading ? "Submitting…" : "Submit"} <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </form>
  );
}
